/**
 * Demo Notification System
 *
 * Delivers timed in-app notifications that follow the "Failure Scenario" demo guide
 * (6 acts: data pipeline failure -> cost spike -> model regression -> agent safety violation -> incident -> resolution).
 *
 * Entirely gated behind IS_STATIC_DEMO. Session start time is persisted in localStorage
 * so notifications survive page refreshes mid-session.
 */

const LS_SESSION_START = 'demo_notification_session_start';
const LS_READ_IDS = 'demo_notification_read_ids';
const LS_DELETED_IDS = 'demo_notification_deleted_ids';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface NotificationScheduleEntry {
  id: string;
  delay_seconds: number;
  title: string;
  message: string;
  notification_type: string;
  severity: string;
  category: string;
  action_url: string;
}

interface DemoNotification {
  id: string;
  title: string;
  message: string;
  notification_type: string;
  severity: string;
  category: string;
  action_url: string;
  is_read: boolean;
  created_at: string;
  updated_at: string;
}

interface DemoNotificationFilter {
  unreadOnly?: boolean;
  category?: string;
}

interface DemoNotificationsResult {
  notifications: DemoNotification[];
  total: number;
  unread_count: number;
}

// ---------------------------------------------------------------------------
// Notification schedule — ordered by delay_seconds
// ---------------------------------------------------------------------------
const NOTIFICATION_SCHEDULE: NotificationScheduleEntry[] = [
  {
    id: 'demo-notif-001',
    delay_seconds: 5,
    title: 'Pipeline run failed: customer_interactions_refresh',
    message: 'Redshift cluster ai-analytics-prod unreachable. customer-interactions-v3 dataset stale.',
    notification_type: 'pipeline_failure',
    severity: 'critical',
    category: 'dataops',
    action_url: '/dataops/runs',
  },
  {
    id: 'demo-notif-002',
    delay_seconds: 25,
    title: 'Budget alert: 20% over last month',
    message: 'Current month $3,480 vs. last month $2,900. Top contributors: SageMaker, Bedrock.',
    notification_type: 'cost_alert',
    severity: 'warning',
    category: 'platformops',
    action_url: '/cloudops/finops',
  },
  {
    id: 'demo-notif-003',
    delay_seconds: 50,
    title: 'Cost anomaly: Amazon Bedrock +1,480%',
    message: 'Bedrock daily spend jumped from $18 baseline to $285. InvokeModel usage in production.',
    notification_type: 'anomaly_detected',
    severity: 'warning',
    category: 'platformops',
    action_url: '/cloudops/anomalies',
  },
  {
    id: 'demo-notif-004',
    delay_seconds: 80,
    title: 'Evaluation regression: product-recommendation',
    message: 'Score dropped from 0.92 to 0.71. Accuracy 0.68, relevance 0.72 — below threshold.',
    notification_type: 'evaluation_regression',
    severity: 'warning',
    category: 'genaiops',
    action_url: '/ai-operations/evaluation',
  },
  {
    id: 'demo-notif-005',
    delay_seconds: 95,
    title: 'Degraded session: product-recommendation',
    message: 'Session flagged degraded. 11,936 tokens, avg latency 2,890ms. Cost $0.121.',
    notification_type: 'model_degradation',
    severity: 'warning',
    category: 'genaiops',
    action_url: '/ai-operations/observability',
  },
  {
    id: 'demo-notif-006',
    delay_seconds: 115,
    title: 'Safety violation: customer-support-agent',
    message: 'Unauthorized action: agent executed override_priority bypassing human-in-the-loop. Classification confidence 0.41.',
    notification_type: 'guardrail_violation',
    severity: 'critical',
    category: 'agentops',
    action_url: '/ai-operations/safety',
  },
  {
    id: 'demo-notif-007',
    delay_seconds: 135,
    title: 'New incident: customer-support-agent safety violation',
    message: 'Unauthorized action with low confidence classification. Stale data from customer-interactions-v3 suspected.',
    notification_type: 'agent_incident',
    severity: 'critical',
    category: 'agentops',
    action_url: '/ai-operations/incidents',
  },
];

// ---------------------------------------------------------------------------
// Session management
// ---------------------------------------------------------------------------

export function startDemoNotificationSession(): void {
  localStorage.setItem(LS_SESSION_START, String(Date.now()));
  localStorage.removeItem(LS_READ_IDS);
  localStorage.removeItem(LS_DELETED_IDS);
  sessionStorage.removeItem('notification_toasted_ids');
}

export function clearDemoNotificationSession(): void {
  localStorage.removeItem(LS_SESSION_START);
  localStorage.removeItem(LS_READ_IDS);
  localStorage.removeItem(LS_DELETED_IDS);
  sessionStorage.removeItem('notification_toasted_ids');
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function getSessionStart(): number | null {
  const raw = localStorage.getItem(LS_SESSION_START);
  return raw ? Number(raw) : null;
}

function getReadIds(): string[] {
  try {
    return JSON.parse(localStorage.getItem(LS_READ_IDS) || '[]');
  } catch {
    return [];
  }
}

function getDeletedIds(): string[] {
  try {
    return JSON.parse(localStorage.getItem(LS_DELETED_IDS) || '[]');
  } catch {
    return [];
  }
}

function elapsedSeconds(): number {
  const start = getSessionStart();
  if (!start) return 0;
  return (Date.now() - start) / 1000;
}

function buildNotification(entry: NotificationScheduleEntry, readIds: string[]): DemoNotification {
  const sessionStart = getSessionStart() || Date.now();
  return {
    id: entry.id,
    title: entry.title,
    message: entry.message,
    notification_type: entry.notification_type,
    severity: entry.severity,
    category: entry.category,
    action_url: entry.action_url,
    is_read: readIds.includes(entry.id),
    created_at: new Date(sessionStart + entry.delay_seconds * 1000).toISOString(),
    updated_at: new Date(sessionStart + entry.delay_seconds * 1000).toISOString(),
  };
}

// ---------------------------------------------------------------------------
// Public query API
// ---------------------------------------------------------------------------

/**
 * Returns notifications whose delay has elapsed, minus deleted ones.
 * Supports optional filter: { unreadOnly: boolean, category: string }
 */
export function getDemoNotifications(filter: DemoNotificationFilter = {}): DemoNotificationsResult {
  const elapsed = elapsedSeconds();
  const readIds = getReadIds();
  const deletedIds = getDeletedIds();

  let delivered = NOTIFICATION_SCHEDULE
    .filter(n => n.delay_seconds <= elapsed && !deletedIds.includes(n.id))
    .map(n => buildNotification(n, readIds));

  if (filter.unreadOnly) {
    delivered = delivered.filter(n => !n.is_read);
  }
  if (filter.category) {
    delivered = delivered.filter(n => n.category === filter.category);
  }

  // Most recent first
  delivered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const unreadCount = delivered.filter(n => !n.is_read).length;

  return {
    notifications: delivered,
    total: delivered.length,
    unread_count: unreadCount,
  };
}

export function getDemoUnreadCount(): number {
  const elapsed = elapsedSeconds();
  const readIds = getReadIds();
  const deletedIds = getDeletedIds();

  return NOTIFICATION_SCHEDULE.filter(
    n => n.delay_seconds <= elapsed && !deletedIds.includes(n.id) && !readIds.includes(n.id)
  ).length;
}

// ---------------------------------------------------------------------------
// Mutation API (persisted to localStorage)
// ---------------------------------------------------------------------------

export function markDemoNotificationRead(id: string): void {
  const readIds = getReadIds();
  if (!readIds.includes(id)) {
    readIds.push(id);
    localStorage.setItem(LS_READ_IDS, JSON.stringify(readIds));
  }
}

export function markAllDemoNotificationsRead(): void {
  const elapsed = elapsedSeconds();
  const deletedIds = getDeletedIds();
  const readIds = NOTIFICATION_SCHEDULE
    .filter(n => n.delay_seconds <= elapsed && !deletedIds.includes(n.id))
    .map(n => n.id);
  localStorage.setItem(LS_READ_IDS, JSON.stringify(readIds));
}

export function deleteDemoNotification(id: string): void {
  const deletedIds = getDeletedIds();
  if (!deletedIds.includes(id)) {
    deletedIds.push(id);
    localStorage.setItem(LS_DELETED_IDS, JSON.stringify(deletedIds));
  }
}

// ---------------------------------------------------------------------------
// Smart polling helper
// ---------------------------------------------------------------------------

/**
 * Returns milliseconds until the next notification arrives, or null if all
 * have already been delivered.
 */
export function getNextDeliveryIn(): number | null {
  const start = getSessionStart();
  if (!start) return null;

  const now = Date.now();
  const elapsed = (now - start) / 1000;

  const next = NOTIFICATION_SCHEDULE.find(n => n.delay_seconds > elapsed);
  if (!next) return null;

  return Math.max(0, (next.delay_seconds * 1000) - (now - start));
}
